import React, { useState } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Select,
  Option,
  FormControl,
  FormLabel,
  Textarea,
  Chip,
  Stack,
  IconButton,
  CircularProgress,
  Divider,
  Card,
  LinearProgress,
  Tooltip,
  Sheet,
} from '@mui/joy';
import {
  Sparkle as AIIcon,
  ArrowsClockwise as RefreshIcon,
  Check as CheckIcon,
  X as CloseIcon,
  Brain as ToneIcon,
  Gauge as SpeedIcon,
  Lightbulb as IdeaIcon,
  Lightbulb as TipsIcon,
} from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface AIContentPanelProps {
  open: boolean;
  onClose: () => void;
  sectionType?: string;
  fields?: any[];
  currentData?: Record<string, any>;
  aiSettings?: {
    tone?: string;
    style?: string;
    keywords?: string[];
  };
  contentBrief?: string;
  onGenerate: (generatedContent: Record<string, any>) => void;
}

interface GeneratedField {
  name: string;
  value: string;
  status: 'pending' | 'generating' | 'generated' | 'accepted' | 'rejected';
  originalValue?: string;
}

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional', icon: '💼' },
  { value: 'friendly', label: 'Friendly', icon: '😊' },
  { value: 'conversational', label: 'Conversational', icon: '💬' },
  { value: 'urgent', label: 'Urgent', icon: '⚡' },
  { value: 'educational', label: 'Educational', icon: '📚' },
  { value: 'inspirational', label: 'Inspirational', icon: '✨' },
];

const STYLE_OPTIONS = [
  { value: 'concise', label: 'Concise' },
  { value: 'detailed', label: 'Detailed' },
  { value: 'storytelling', label: 'Storytelling' },
  { value: 'technical', label: 'Technical' },
];

export const AIContentPanel: React.FC<AIContentPanelProps> = ({
  open,
  onClose,
  sectionType,
  fields = [],
  currentData = {},
  aiSettings = {},
  contentBrief = '',
  onGenerate,
}) => {
  const [tone, setTone] = useState(aiSettings.tone || 'professional');
  const [style, setStyle] = useState(aiSettings.style || 'concise');
  const [customPrompt, setCustomPrompt] = useState('');
  const [generatedFields, setGeneratedFields] = useState<GeneratedField[]>([]);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [progress, setProgress] = useState(0);

  // Initialize generated fields from current data
  React.useEffect(() => {
    if (open && fields.length > 0) {
      setGeneratedFields(
        fields.map(field => ({
          name: field.name,
          value: currentData[field.name] || '',
          originalValue: currentData[field.name] || '',
          status: 'pending' as const,
        }))
      );
    }
  }, [open, fields, currentData]);

  // Generate single field mutation
  const generateFieldMutation = useMutation({
    mutationFn: async ({ fieldName, regenerate = false }: { fieldName: string; regenerate?: boolean }) => {
      return api.post('/api/cms/ai/generate-field/', {
        section_type: sectionType,
        field_name: fieldName,
        current_data: currentData,
        ai_settings: { tone, style, keywords: aiSettings.keywords },
        content_brief: contentBrief || customPrompt,
        regenerate,
      });
    },
    onSuccess: (response: any, { fieldName }: { fieldName: string; regenerate?: boolean }) => {
      const data = response?.data || response;
      if (data?.value) {
        setGeneratedFields((prev: any) =>
          prev.map((field: any) =>
            field.name === fieldName
              ? { ...field, value: data.value, status: 'generated' as const }
              : field
          )
        );
      }
    },
    onError: (_, { fieldName }: { fieldName: string; regenerate?: boolean }) => {
      setGeneratedFields(prev =>
        prev.map(field =>
          field.name === fieldName
            ? { ...field, status: 'rejected' as const }
            : field
        )
      );
    },
  });

  // Generate all content mutation
  const generateAllMutation = useMutation({
    mutationFn: async () => {
      return api.post('/api/cms/ai/generate-page/', {
        section_type: sectionType,
        fields: fields.map(f => f.name),
        ai_settings: { tone, style, keywords: aiSettings.keywords },
        content_brief: contentBrief || customPrompt,
      });
    },
    onSuccess: (response: any) => {
      const data = response?.data || response;
      if (data?.content) {
        setGeneratedFields((prev: any) =>
          prev.map((field: any) => ({
            ...field,
            value: data.content[field.name] || field.value,
            status: data.content[field.name] ? 'generated' as const : field.status,
          }))
        );
      }
      setIsGeneratingAll(false);
      setProgress(100);
    },
    onError: () => {
      setIsGeneratingAll(false);
      setProgress(0);
    },
  });

  const handleGenerateField = async (fieldName: string, regenerate = false) => {
    setGeneratedFields(prev =>
      prev.map(field =>
        field.name === fieldName
          ? { ...field, status: 'generating' as const }
          : field
      )
    );
    await generateFieldMutation.mutateAsync({ fieldName, regenerate });
  };

  const handleGenerateAll = async () => {
    setIsGeneratingAll(true);
    setProgress(0);
    
    // Set all fields to generating
    setGeneratedFields(prev =>
      prev.map(field => ({ ...field, status: 'generating' as const }))
    );

    // Simulate progress
    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + 10, 90));
    }, 500);

    await generateAllMutation.mutateAsync();
    
    clearInterval(progressInterval);
    setProgress(100);
    setTimeout(() => setProgress(0), 1000);
  };

  const handleFieldEdit = (fieldName: string, value: string) => {
    setGeneratedFields(prev =>
      prev.map(field =>
        field.name === fieldName
          ? { ...field, value, status: 'generated' as const }
          : field
      )
    );
  };

  const handleAcceptField = (fieldName: string) => {
    setGeneratedFields(prev =>
      prev.map(field =>
        field.name === fieldName
          ? { ...field, status: 'accepted' as const }
          : field
      )
    );
  };

  const handleRejectField = (fieldName: string) => {
    setGeneratedFields(prev =>
      prev.map(field =>
        field.name === fieldName
          ? { ...field, value: field.originalValue || '', status: 'rejected' as const }
          : field
      )
    );
  };

  const handleApplyAll = () => {
    const acceptedContent: Record<string, any> = {};
    generatedFields.forEach(field => {
      if (field.status === 'accepted' || field.status === 'generated') {
        acceptedContent[field.name] = field.value;
      }
    });
    onGenerate(acceptedContent);
    onClose();
  };

  const acceptedCount = generatedFields.filter(f => 
    f.status === 'accepted' || f.status === 'generated'
  ).length;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" sx={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden' }}>
        <ModalClose />
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AIIcon color="var(--joy-palette-primary-500)" />
          <Typography level="h4">AI Content Generator</Typography>
          {sectionType && (
            <Chip size="sm" variant="soft" color="primary">
              {sectionType}
            </Chip>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(90vh - 180px)' }}>
          {/* Settings Panel */}
          <Card variant="soft" sx={{ mb: 2 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ToneIcon />
                      <span>Tone</span>
                    </Box>
                  </FormLabel>
                  <Select value={tone} onChange={(_, value) => setTone(value as string)}>
                    {TONE_OPTIONS.map(option => (
                      <Option key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{option.icon}</span>
                          <span>{option.label}</span>
                        </Box>
                      </Option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SpeedIcon />
                      <span>Style</span>
                    </Box>
                  </FormLabel>
                  <Select value={style} onChange={(_, value) => setStyle(value as string)}>
                    {STYLE_OPTIONS.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <FormControl>
                <FormLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IdeaIcon />
                    <span>Additional Context</span>
                    <Tooltip title="Provide extra context to guide the AI">
                      <TipsIcon size={16} style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </Box>
                </FormLabel>
                <Textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="E.g., Focus on residential roofing, emphasize 25 years of experience..."
                  minRows={2}
                  maxRows={3}
                />
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="solid"
                  color="primary"
                  onClick={handleGenerateAll}
                  loading={isGeneratingAll}
                  startDecorator={<AIIcon />}
                  sx={{ flex: 1 }}
                >
                  Generate All Content
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setGeneratedFields(prev =>
                      prev.map(field => ({
                        ...field,
                        value: field.originalValue || '',
                        status: 'pending' as const,
                      }))
                    );
                  }}
                  disabled={isGeneratingAll}
                >
                  Reset All
                </Button>
              </Box>

              {progress > 0 && (
                <LinearProgress
                  determinate
                  value={progress}
                  thickness={4}
                  sx={{ mt: 1 }}
                />
              )}
            </Stack>
          </Card>

          {/* Fields List */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Stack spacing={2}>
              {generatedFields.map((field) => {
                const fieldConfig = fields.find(f => f.name === field.name);
                return (
                  <Card
                    key={field.name}
                    variant={field.status === 'accepted' ? 'soft' : 'outlined'}
                    color={field.status === 'accepted' ? 'success' : 'neutral'}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography level="body-sm" fontWeight="md" sx={{ mb: 1 }}>
                          {fieldConfig?.label || field.name}
                          {field.status === 'generating' && (
                            <CircularProgress size="sm" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                        
                        {field.status === 'generated' || field.status === 'accepted' ? (
                          <Textarea
                            value={field.value}
                            onChange={(e) => handleFieldEdit(field.name, e.target.value)}
                            minRows={2}
                            maxRows={4}
                            sx={{
                              bgcolor: field.status === 'accepted' ? 'success.50' : 'background.surface',
                            }}
                          />
                        ) : (
                          <Sheet variant="soft" sx={{ p: 2, borderRadius: 'sm' }}>
                            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                              {field.value || 'Click generate to create content...'}
                            </Typography>
                          </Sheet>
                        )}
                      </Box>

                      <Stack direction="row" spacing={0.5}>
                        {field.status !== 'generating' && (
                          <>
                            <Tooltip title="Generate">
                              <IconButton
                                size="sm"
                                variant={field.status === 'pending' ? 'solid' : 'outlined'}
                                color="primary"
                                onClick={() => handleGenerateField(field.name, field.status === 'generated')}
                              >
                                {field.status === 'generated' ? <RefreshIcon /> : <AIIcon />}
                              </IconButton>
                            </Tooltip>

                            {(field.status === 'generated' || field.status === 'accepted') && (
                              <>
                                <Tooltip title="Accept">
                                  <IconButton
                                    size="sm"
                                    variant={field.status === 'accepted' ? 'solid' : 'outlined'}
                                    color="success"
                                    onClick={() => handleAcceptField(field.name)}
                                  >
                                    <CheckIcon />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Reject">
                                  <IconButton
                                    size="sm"
                                    variant="outlined"
                                    color="danger"
                                    onClick={() => handleRejectField(field.name)}
                                  >
                                    <CloseIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                      </Stack>
                    </Box>
                  </Card>
                );
              })}
            </Stack>
          </Box>

          {/* Action Footer */}
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {acceptedCount} of {generatedFields.length} fields ready to apply
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="solid"
                color="success"
                onClick={handleApplyAll}
                disabled={acceptedCount === 0}
                startDecorator={<CheckIcon />}
              >
                Apply {acceptedCount > 0 ? acceptedCount : ''} Field{acceptedCount !== 1 ? 's' : ''}
              </Button>
            </Box>
          </Box>
        </Box>
      </ModalDialog>
    </Modal>
  );
};